import { useMemo } from 'react';
import type { ShelfEntryView } from '@/features/library/api/library.api.js';
import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';
import { useReadingActivity } from '@/features/library/hooks/useReadingActivity.js';
import { useMyFollows } from '@/features/follows/hooks/useMyFollows.js';
import { useMyCollections } from '@/features/collections/hooks/useMyCollections.js';

interface BadgeDef {
  readonly id: string;
  readonly icon: string;
  readonly name: string;
  /** Critère d'obtention montré au survol. */
  readonly description: string;
  /** Indicateur de progression "X / Y" optionnel. */
  readonly progress?: (deps: BadgeDeps) => { current: number; goal: number };
  readonly check: (deps: BadgeDeps) => boolean;
}

interface BadgeDeps {
  readonly entries: ReadonlyArray<ShelfEntryView>;
  readonly streak: number;
  readonly followsCount: number;
  readonly collectionsCount: number;
}

/** Comptage par genre (clé Booknest) parmi les livres "done". */
function countDoneByGenre(entries: ReadonlyArray<ShelfEntryView>, genres: ReadonlyArray<string>): number {
  return entries.filter(
    (e) => e.status === 'done' && genres.includes(e.book.genre),
  ).length;
}

const BADGES: ReadonlyArray<BadgeDef> = [
  {
    id: 'first-book',
    icon: '🌱',
    name: 'Premier livre',
    description: 'Termine ton tout premier livre',
    progress: ({ entries }) => ({
      current: entries.filter((e) => e.status === 'done').length,
      goal: 1,
    }),
    check: ({ entries }) =>
      entries.some((e) => e.status === 'done'),
  },
  {
    id: 'five-books',
    icon: '🦉',
    name: 'Hibou de nuit',
    description: 'Termine 5 livres au total',
    progress: ({ entries }) => ({
      current: entries.filter((e) => e.status === 'done').length,
      goal: 5,
    }),
    check: ({ entries }) =>
      entries.filter((e) => e.status === 'done').length >= 5,
  },
  {
    id: 'twentyfive-books',
    icon: '📖',
    name: 'Bibliophile',
    description: 'Termine 25 livres au total',
    progress: ({ entries }) => ({
      current: entries.filter((e) => e.status === 'done').length,
      goal: 25,
    }),
    check: ({ entries }) =>
      entries.filter((e) => e.status === 'done').length >= 25,
  },
  {
    id: 'fifty-books',
    icon: '🏆',
    name: 'Demi-centenaire',
    description: 'Termine 50 livres au total',
    progress: ({ entries }) => ({
      current: entries.filter((e) => e.status === 'done').length,
      goal: 50,
    }),
    check: ({ entries }) =>
      entries.filter((e) => e.status === 'done').length >= 50,
  },
  {
    id: 'hundred-books',
    icon: '👑',
    name: 'Centenaire',
    description: 'Termine 100 livres au total',
    progress: ({ entries }) => ({
      current: entries.filter((e) => e.status === 'done').length,
      goal: 100,
    }),
    check: ({ entries }) =>
      entries.filter((e) => e.status === 'done').length >= 100,
  },
  {
    id: 'critic',
    icon: '⭐',
    name: "Critique d'or",
    description: 'Note 10 livres',
    progress: ({ entries }) => ({
      current: entries.filter((e) => e.rating !== null).length,
      goal: 10,
    }),
    check: ({ entries }) =>
      entries.filter((e) => e.rating !== null).length >= 10,
  },
  {
    id: 'perfect-five',
    icon: '✒️',
    name: 'Plume affûtée',
    description: 'Donne 5 fois la note maximale (5★)',
    progress: ({ entries }) => ({
      current: entries.filter((e) => e.rating === 5).length,
      goal: 5,
    }),
    check: ({ entries }) =>
      entries.filter((e) => e.rating === 5).length >= 5,
  },
  {
    id: 'wishlist-twenty',
    icon: '💌',
    name: 'Pile à lire',
    description: 'Ajoute 20 livres à ta wishlist',
    progress: ({ entries }) => ({
      current: entries.filter((e) => e.status === 'wishlist').length,
      goal: 20,
    }),
    check: ({ entries }) =>
      entries.filter((e) => e.status === 'wishlist').length >= 20,
  },
  {
    id: 'romance-5',
    icon: '🌹',
    name: 'Cœur romantique',
    description: 'Termine 5 livres de Romance (toutes variantes)',
    progress: ({ entries }) => ({
      current: countDoneByGenre(entries, ['romance', 'dark-romance', 'romantasy']),
      goal: 5,
    }),
    check: ({ entries }) =>
      countDoneByGenre(entries, ['romance', 'dark-romance', 'romantasy']) >= 5,
  },
  {
    id: 'fantasy-5',
    icon: '🐉',
    name: 'Dompteuse',
    description: 'Termine 5 livres de Fantasy / Romantasy',
    progress: ({ entries }) => ({
      current: countDoneByGenre(entries, ['fantasy', 'romantasy']),
      goal: 5,
    }),
    check: ({ entries }) =>
      countDoneByGenre(entries, ['fantasy', 'romantasy']) >= 5,
  },
  {
    id: 'thriller-5',
    icon: '🔪',
    name: 'Frissons',
    description: 'Termine 5 livres de Thriller / Horror / Policier',
    progress: ({ entries }) => ({
      current: countDoneByGenre(entries, ['thriller', 'horror', 'policier', 'mystery']),
      goal: 5,
    }),
    check: ({ entries }) =>
      countDoneByGenre(entries, ['thriller', 'horror', 'policier', 'mystery']) >= 5,
  },
  {
    id: 'french-lit-5',
    icon: '🇫🇷',
    name: 'Tricolore',
    description: 'Termine 5 livres de Littérature française',
    progress: ({ entries }) => ({
      current: countDoneByGenre(entries, ['litterature-fr', 'classique']),
      goal: 5,
    }),
    check: ({ entries }) =>
      countDoneByGenre(entries, ['litterature-fr', 'classique']) >= 5,
  },
  {
    id: 'cosy-5',
    icon: '☕',
    name: 'Cosy reader',
    description: 'Termine 5 livres Feel-good',
    progress: ({ entries }) => ({
      current: countDoneByGenre(entries, ['feel-good']),
      goal: 5,
    }),
    check: ({ entries }) =>
      countDoneByGenre(entries, ['feel-good']) >= 5,
  },
  {
    id: 'streak-7',
    icon: '🔥',
    name: 'Régulière',
    description: '7 jours consécutifs de lecture',
    progress: ({ streak }) => ({ current: streak, goal: 7 }),
    check: ({ streak }) => streak >= 7,
  },
  {
    id: 'streak-30',
    icon: '🌟',
    name: 'Marathonienne',
    description: '30 jours consécutifs de lecture',
    progress: ({ streak }) => ({ current: streak, goal: 30 }),
    check: ({ streak }) => streak >= 30,
  },
  {
    id: 'polyglot',
    icon: '🪶',
    name: 'Éclectique',
    description: 'Termine au moins un livre dans 5 genres différents',
    progress: ({ entries }) => {
      const set = new Set(
        entries.filter((e) => e.status === 'done').map((e) => e.book.genre),
      );
      return { current: set.size, goal: 5 };
    },
    check: ({ entries }) => {
      const set = new Set(
        entries.filter((e) => e.status === 'done').map((e) => e.book.genre),
      );
      return set.size >= 5;
    },
  },
  {
    id: 'follows-5',
    icon: '👀',
    name: 'Découvreuse',
    description: 'Suis 5 auteurs ou genres',
    progress: ({ followsCount }) => ({ current: followsCount, goal: 5 }),
    check: ({ followsCount }) => followsCount >= 5,
  },
  {
    id: 'collections-3',
    icon: '🧺',
    name: 'Collectionneuse',
    description: 'Crée 3 collections thématiques',
    progress: ({ collectionsCount }) => ({
      current: collectionsCount,
      goal: 3,
    }),
    check: ({ collectionsCount }) => collectionsCount >= 3,
  },
];

/**
 * Calcule la plus longue séquence de jours consécutifs avec activité de
 * lecture > 0 dans le calendrier reading-activity (52 semaines glissantes).
 */
function computeStreak(cells: ReadonlyArray<{ level: number }>): number {
  let max = 0;
  let current = 0;
  for (const c of cells) {
    if (c.level > 0) {
      current += 1;
      if (current > max) max = current;
    } else {
      current = 0;
    }
  }
  return max;
}

export function BadgeList(): JSX.Element {
  const { data: shelfData } = useMyShelf();
  const { data: activityData } = useReadingActivity();
  const { data: followsData } = useMyFollows();
  const { data: collectionsData } = useMyCollections();

  const deps: BadgeDeps = useMemo(
    () => ({
      entries: shelfData?.items ?? [],
      streak: activityData?.cells ? computeStreak(activityData.cells) : 0,
      followsCount:
        (followsData?.authors.length ?? 0) + (followsData?.genres.length ?? 0),
      collectionsCount: collectionsData?.items.length ?? 0,
    }),
    [shelfData, activityData, followsData, collectionsData],
  );

  const badges = useMemo(
    () =>
      BADGES.map((b) => ({
        ...b,
        unlocked: b.check(deps),
        progressVal: b.progress?.(deps) ?? null,
      })),
    [deps],
  );

  const unlocked = badges.filter((b) => b.unlocked).length;

  return (
    <section className="rounded-3xl bg-paper p-5 shadow-md">
      <div className="mb-3">
        <h3 className="font-serif text-base font-semibold text-brown">
          🏅 Tes badges
        </h3>
        <p className="font-script text-sm text-brown-soft">
          {unlocked} / {badges.length} débloqués
        </p>
      </div>
      <ul className="grid grid-cols-3 gap-3">
        {badges.map((b) => (
          <li key={b.id} className="group relative text-center">
            <div
              tabIndex={0}
              className={`mx-auto mb-1.5 flex h-12 w-12 cursor-help items-center justify-center rounded-full text-xl transition-transform hover:scale-110 focus:outline-none focus:scale-110 ${
                b.unlocked
                  ? 'bg-gradient-to-br from-gold-light to-gold shadow-md shadow-gold/30'
                  : 'bg-cream-dark text-brown-light opacity-50 grayscale'
              }`}
              aria-describedby={`badge-tip-${b.id}`}
            >
              {b.icon}
            </div>
            <p
              className={`font-serif text-[10px] font-semibold leading-tight ${
                b.unlocked ? 'text-brown' : 'text-brown-light'
              }`}
            >
              {b.name}
            </p>

            <div
              id={`badge-tip-${b.id}`}
              role="tooltip"
              className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-44 -translate-x-1/2 rounded-xl bg-brown px-3 py-2 text-[11px] leading-snug text-cream-light shadow-lg group-hover:block group-focus-within:block"
            >
              <p className="font-serif font-semibold">{b.name}</p>
              <p className="mt-0.5 opacity-90">{b.description}</p>
              {b.progressVal && (
                <p className="mt-1 font-semibold text-gold-light">
                  {Math.min(b.progressVal.current, b.progressVal.goal)} / {b.progressVal.goal}
                </p>
              )}
              <span
                aria-hidden
                className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-brown"
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
